import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box, Typography, Paper, Stack, Button, Chip, Skeleton, Alert, ButtonBase, LinearProgress, Avatar, Tooltip,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import apiService from '../../services/apiServices';
import usePermissions from '../../hooks/usePermissions';

/**
 * Rekrutimi · Përmbledhje — hyrja e modulit.
 *
 * Rekrutimi hapej drejt te lista e konkurseve: për të ditur çka pret veprim
 * duhej hapur çdo faqe me radhë. Kjo faqe tregon vetëm atë që kërkon vëmendje
 * sot — kandidatët e pashqyrtuar, ofertat në pritje, pozitat që mbyllen — dhe
 * çdo kartë/rresht të çon aty ku bëhet puna. Shifrat vijnë të agreguara nga
 * serveri (`/api/recruitment/overview`); asgjë s'llogaritet mbi listën e plotë.
 *
 * Qëllimisht mungojnë «Intervista këtë javë» dhe «Kërkesa për pozita»: intervistat
 * s'kanë ende datë dhe kërkesat s'ekzistojnë ende si entitet. Do të shtohen kur
 * të dhënat t'i mbajnë, jo si numra të sajuar.
 */

const STAGE_LABEL = {
  applied: 'Aplikuar', shortlisted: 'Listë e shkurtër', interview: 'Intervistë', offer: 'Ofertë', hired: 'Punësuar',
};

function ago(d) {
  if (!d) return '';
  const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  if (days <= 0) return 'sot';
  if (days === 1) return 'dje';
  return `para ${days} ditësh`;
}
function daysLeft(d) {
  if (!d) return null;
  const end = new Date(`${d}T23:59:59`);
  return Math.ceil((end - Date.now()) / 86400000);
}
// Emrat e muajve me dorë: shumë shfletues s'e kanë lokalin `sq-AL` dhe kthejnë anglisht.
const MONTHS = ['janar', 'shkurt', 'mars', 'prill', 'maj', 'qershor', 'korrik', 'gusht', 'shtator', 'tetor', 'nëntor', 'dhjetor'];
const thisMonth = () => { const d = new Date(); return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`; };
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('sq-AL', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
const initials = (n) => (n || '?').split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase() || '?';

function Kpi({ icon: Icon, label, value, hint, color, onClick, loading }) {
  const theme = useTheme();
  return (
    <ButtonBase onClick={onClick} disabled={!onClick}
      sx={{ display: 'block', textAlign: 'left', borderRadius: 3, width: '100%' }}>
      <Paper variant="outlined" sx={{
        p: 1.75, borderRadius: 3, height: '100%', display: 'flex', alignItems: 'center', gap: 1.5,
        transition: 'border-color .15s, box-shadow .15s',
        ...(onClick ? { '&:hover': { borderColor: color, boxShadow: `0 4px 14px ${alpha(color, 0.14)}` } } : {}),
      }}>
        <Box sx={{ width: 38, height: 38, borderRadius: 2, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha(color, theme.palette.mode === 'dark' ? 0.25 : 0.12) }}>
          <Icon sx={{ fontSize: 20, color }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: '.62rem', fontWeight: 700, letterSpacing: '.05em', color: 'text.secondary', textTransform: 'uppercase' }}>{label}</Typography>
          {loading ? <Skeleton width={40} height={30} /> : (
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, lineHeight: 1.2 }}>{value}</Typography>
          )}
          {hint && !loading && <Typography noWrap sx={{ fontSize: '.68rem', color: 'text.secondary' }}>{hint}</Typography>}
        </Box>
      </Paper>
    </ButtonBase>
  );
}

function Section({ title, count, action, children, empty, emptyCta }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography sx={{ fontWeight: 800, fontSize: '.88rem', flex: 1 }}>
          {title}{typeof count === 'number' && <Box component="span" sx={{ ml: 0.75, color: 'text.secondary', fontWeight: 700 }}>{count}</Box>}
        </Typography>
        {action}
      </Stack>
      {empty ? (
        <Stack alignItems="center" spacing={1} sx={{ py: 3.5, px: 2, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '.8rem', color: 'text.secondary' }}>{empty}</Typography>
          {emptyCta}
        </Stack>
      ) : children}
    </Paper>
  );
}

function Row({ onClick, avatar, title, subtitle, right }) {
  return (
    <ButtonBase onClick={onClick} sx={{
      display: 'flex', width: '100%', justifyContent: 'flex-start', textAlign: 'left', gap: 1.25, px: 2, py: 1.1,
      '&:not(:last-of-type)': { borderBottom: '1px solid', borderColor: 'divider' },
      '&:hover': { bgcolor: 'action.hover' },
    }}>
      {avatar}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: '.82rem', fontWeight: 700 }}>{title}</Typography>
        {subtitle && <Typography noWrap sx={{ fontSize: '.7rem', color: 'text.secondary' }}>{subtitle}</Typography>}
      </Box>
      {right}
      <ChevronRightRoundedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
    </ButtonBase>
  );
}

const PersonAvatar = ({ name }) => (
  <Avatar sx={{ width: 30, height: 30, fontSize: '.7rem', fontWeight: 800 }}>{initials(name)}</Avatar>
);

export default function RecruitmentOverview() {
  const theme = useTheme();
  const history = useHistory();
  const { can } = usePermissions();
  const canCreate = can('recruitment.create') || can('recruitment.manage');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true); setError('');
    apiService().get('recruitment/overview')
      .then((r) => { if (r?.success) setData(r.data); else setError(r?.error || 'Përmbledhja nuk u ngarkua.'); })
      .catch(() => setError('Përmbledhja nuk u ngarkua.'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const k = data?.kpis || {};
  const go = (to) => () => history.push(to);
  const openCandidate = (id) => () => history.push(`/applications/edit/${id}`);
  const funnel = data?.funnel || [];
  const top = Math.max(1, funnel[0]?.count || 0);
  const brand = theme.palette.primary.main;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, minWidth: 0 }}>
      {/* Koka */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5, flexWrap: 'wrap', rowGap: 1 }}>
        <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: alpha(brand, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <QueryStatsRoundedIcon sx={{ color: brand }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.15rem' }}>Përmbledhja e rekrutimit</Typography>
          <Typography sx={{ fontSize: '.8rem', color: 'text.secondary' }}>Çka kërkon vëmendje sot — pozitat, kandidatët dhe hapat e radhës.</Typography>
        </Box>
        <Button size="small" startIcon={<RefreshRoundedIcon />} onClick={load} disabled={loading} sx={{ textTransform: 'none' }}>Rifresko</Button>
        {canCreate && (
          <Button size="small" variant="contained" startIcon={<AddRoundedIcon />} onClick={go('/job-postings')}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>Pozitë e re</Button>
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={load}>Provo sërish</Button>}>{error}</Alert>}

      {/* KPI */}
      <Box sx={{ display: 'grid', gap: 1.5, mb: 2.5, gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', md: 'repeat(3, minmax(0,1fr))', xl: 'repeat(6, minmax(0,1fr))' } }}>
        <Kpi loading={loading} icon={WorkOutlineRoundedIcon} color="#3E8FA3" label="Pozita aktive" value={k.activePositions ?? 0}
          hint={k.openSeats ? `${k.openSeats} ${k.openSeats === 1 ? 'vend' : 'vende'} për të plotësuar` : 'pranojnë aplikime'}
          onClick={go('/job-postings')} />
        <Kpi loading={loading} icon={PeopleAltRoundedIcon} color="#6366F1" label="Kandidatë aktivë" value={k.activeCandidates ?? 0}
          hint={`${k.newThisWeek || 0} të rinj këtë javë`} onClick={go('/applications')} />
        <Kpi loading={loading} icon={HourglassTopRoundedIcon} color="#D97706" label="Pa shqyrtuar" value={k.needsScreening ?? 0}
          hint="aplikuar para më shumë se 48 orësh" onClick={go('/applications?status=applied')} />
        <Kpi loading={loading} icon={RecordVoiceOverRoundedIcon} color="#0EA5E9" label="Në intervistë" value={k.inInterview ?? 0}
          hint="kandidatë në këtë fazë" onClick={go('/interviews')} />
        <Kpi loading={loading} icon={LocalOfferRoundedIcon} color="#8B5CF6" label="Oferta aktive" value={k.activeOffers ?? 0}
          hint="presin përgjigje" onClick={go('/applications?status=offer')} />
        <Kpi loading={loading} icon={HowToRegRoundedIcon} color="#16A34A" label="Punësime këtë muaj" value={k.hiresThisMonth ?? 0}
          hint={thisMonth()} onClick={go('/applications?status=hired')} />
      </Box>

      {/* Funnel */}
      <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, mb: 2.5 }}>
        <Stack direction="row" alignItems="baseline" sx={{ mb: 1.5 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '.88rem', flex: 1 }}>Rruga e kandidatëve</Typography>
          <Typography sx={{ fontSize: '.7rem', color: 'text.secondary' }}>
            sa arritën deri te secila fazë{k.rejected ? ` · ${k.rejected} të refuzuar` : ''}
          </Typography>
        </Stack>
        {loading ? <Skeleton variant="rounded" height={72} /> : (
          <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(5, minmax(0,1fr))' } }}>
            {funnel.map((f, i) => {
              const prev = i ? funnel[i - 1].count : null;
              const conv = prev ? Math.round((f.count / prev) * 100) : null;
              return (
                <ButtonBase key={f.stage} onClick={go(`/applications?status=${f.stage}`)}
                  sx={{ display: 'block', textAlign: 'left', borderRadius: 2, p: 1.25, '&:hover': { bgcolor: 'action.hover' } }}>
                  <Typography noWrap sx={{ fontSize: '.72rem', fontWeight: 700, color: 'text.secondary' }}>{STAGE_LABEL[f.stage] || f.stage}</Typography>
                  <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, lineHeight: 1.2 }}>{f.count}</Typography>
                  <LinearProgress variant="determinate" value={(f.count / top) * 100}
                    sx={{ mt: 0.75, height: 6, borderRadius: 3, bgcolor: alpha(brand, 0.1), '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: brand } }} />
                  <Typography sx={{ fontSize: '.66rem', color: 'text.secondary', mt: 0.5, minHeight: 14 }}>
                    {conv !== null && prev > 0 ? `${conv}% e fazës së mëparshme` : ''}
                  </Typography>
                </ButtonBase>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* Seksionet operative */}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0,1fr))' } }}>
        <Section title="Kërkojnë shqyrtim" count={loading ? undefined : k.needsScreening}
          action={<Button size="small" onClick={go('/applications?status=applied')} sx={{ textTransform: 'none' }}>Të gjithë</Button>}
          empty={!loading && !(data?.needsScreening || []).length ? 'Asnjë kandidat s\'pret shqyrtim më shumë se 48 orë.' : null}>
          {loading ? <Box sx={{ p: 2 }}><Skeleton height={120} /></Box> : (data.needsScreening || []).map((c) => (
            <Row key={c.id} onClick={openCandidate(c.id)} avatar={<PersonAvatar name={c.name} />}
              title={c.name || 'Pa emër'} subtitle={c.position || 'Pa pozitë'}
              right={<Chip size="small" label={ago(c.since)} sx={{ height: 20, fontSize: '.64rem', fontWeight: 700, bgcolor: alpha('#D97706', 0.12), color: '#B45309' }} />} />
          ))}
        </Section>

        <Section title="Pozitat aktive" count={loading ? undefined : (data?.positions || []).length}
          action={<Button size="small" onClick={go('/job-postings')} sx={{ textTransform: 'none' }}>Menaxho</Button>}
          empty={!loading && !(data?.positions || []).length ? 'Asnjë pozitë s\'pranon aplikime tani.' : null}
          emptyCta={canCreate ? <Button size="small" variant="outlined" startIcon={<AddRoundedIcon />} onClick={go('/job-postings')} sx={{ textTransform: 'none' }}>Hap një pozitë</Button> : null}>
          {loading ? <Box sx={{ p: 2 }}><Skeleton height={120} /></Box> : (data.positions || []).map((p) => {
            const left = daysLeft(p.deadline);
            return (
              <Row key={p.id} onClick={go('/job-postings')}
                avatar={<Box sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: alpha(brand, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}><WorkOutlineRoundedIcon sx={{ fontSize: 16, color: brand }} /></Box>}
                title={p.title}
                subtitle={`${p.hired || 0} nga ${p.headcount || 1} vende · ${p.active} aktivë · ${p.total} gjithsej${p.newThisWeek ? ` · ${p.newThisWeek} të rinj` : ''}`}
                right={p.upcoming
                  ? <Chip size="small" label={`Hapet ${fmtDate(p.startDate)}`} sx={{ height: 20, fontSize: '.64rem', fontWeight: 700 }} />
                  : left !== null && (
                    <Tooltip title={`Afati: ${fmtDate(p.deadline)}`}>
                      <Chip size="small" label={left <= 0 ? 'mbyllet sot' : `${left} ditë`}
                        sx={{ height: 20, fontSize: '.64rem', fontWeight: 700, ...(left <= 3 ? { bgcolor: alpha('#DC2626', 0.12), color: '#B91C1C' } : {}) }} />
                    </Tooltip>
                  )} />
            );
          })}
        </Section>

        <Section title="Në fazën e intervistës" count={loading ? undefined : k.inInterview}
          action={<Button size="small" onClick={go('/interviews')} sx={{ textTransform: 'none' }}>Intervistat</Button>}
          empty={!loading && !(data?.interviews || []).length ? 'Asnjë kandidat s\'është në fazën e intervistës.' : null}>
          {loading ? <Box sx={{ p: 2 }}><Skeleton height={100} /></Box> : (data.interviews || []).map((c) => (
            <Row key={c.id} onClick={openCandidate(c.id)} avatar={<PersonAvatar name={c.name} />}
              title={c.name || 'Pa emër'} subtitle={c.position || 'Pa pozitë'}
              right={<Typography sx={{ fontSize: '.68rem', color: 'text.secondary' }}>{ago(c.since)}</Typography>} />
          ))}
        </Section>

        <Section title="Oferta në pritje të përgjigjes" count={loading ? undefined : k.activeOffers}
          action={<Button size="small" onClick={go('/applications?status=offer')} sx={{ textTransform: 'none' }}>Të gjitha</Button>}
          empty={!loading && !(data?.offers || []).length ? 'Asnjë ofertë s\'pret përgjigje.' : null}>
          {loading ? <Box sx={{ p: 2 }}><Skeleton height={100} /></Box> : (data.offers || []).map((c) => (
            <Row key={c.id} onClick={openCandidate(c.id)} avatar={<PersonAvatar name={c.name} />}
              title={c.name || 'Pa emër'} subtitle={c.position || 'Pa pozitë'}
              right={<Typography sx={{ fontSize: '.68rem', color: 'text.secondary' }}>dërguar {ago(c.since)}</Typography>} />
          ))}
        </Section>

        {/* Ngarkesa e rekrutuesve — kush e mban radhën, dhe sa e gjatë është.
            Rreshti «Pa rekrutues» rri i pari me qëllim: puna që s'e ka marrë
            askush është pikërisht ajo që humbet pa u vënë re. */}
        <Box sx={{ gridColumn: { lg: '1 / -1' } }}>
          <Section title="Ngarkesa e rekrutuesve" count={loading ? undefined : (data?.workload || []).length}
            action={<Button size="small" onClick={go('/applications')} sx={{ textTransform: 'none' }}>Kandidatët</Button>}
            empty={!loading && !(data?.workload || []).length ? 'Asnjë kandidat aktiv për t\'u ndarë.' : null}>
            {loading ? <Box sx={{ p: 2 }}><Skeleton height={80} /></Box> : (data.workload || []).map((w) => (
              <Row key={w.id || 'unassigned'}
                onClick={go(`/applications?recruiter=${w.id || 'unassigned'}`)}
                avatar={w.id
                  ? <PersonAvatar name={w.name} />
                  : <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: alpha('#D97706', 0.14), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <HourglassTopRoundedIcon sx={{ fontSize: 16, color: '#B45309' }} />
                    </Box>}
                title={w.name}
                subtitle={[
                  `${w.active} aktivë`,
                  w.needsScreening ? `${w.needsScreening} pa shqyrtuar` : null,
                  w.inInterview ? `${w.inInterview} në intervistë` : null,
                  w.activeOffers ? `${w.activeOffers} me ofertë` : null,
                ].filter(Boolean).join(' · ')}
                right={<Chip size="small" label={w.active}
                  sx={{ height: 20, minWidth: 34, fontSize: '.64rem', fontWeight: 800,
                    bgcolor: alpha(w.id ? brand : '#D97706', 0.12), color: w.id ? brand : '#B45309' }} />} />
            ))}
          </Section>
        </Box>

        <Box sx={{ gridColumn: { lg: '1 / -1' } }}>
          <Section title="Onboarding në vazhdim" count={loading ? undefined : (data?.onboarding || []).length}
            action={<Button size="small" onClick={go('/onboarding')} sx={{ textTransform: 'none' }}>Onboarding</Button>}
            empty={!loading && !(data?.onboarding || []).length ? 'Askush s\'ka detyra onboarding të pakryera.' : null}>
            {loading ? <Box sx={{ p: 2 }}><Skeleton height={80} /></Box> : (data.onboarding || []).map((o) => (
              <Row key={o.id} onClick={go('/onboarding')} avatar={<PersonAvatar name={o.name} />}
                title={o.name} subtitle={[o.position, o.nextDue ? `detyra e radhës: ${fmtDate(o.nextDue)}` : null].filter(Boolean).join(' · ')}
                right={(
                  <Box sx={{ width: 120 }}>
                    <Typography sx={{ fontSize: '.66rem', color: 'text.secondary', textAlign: 'right' }}>{o.done} / {o.total}</Typography>
                    <LinearProgress variant="determinate" value={o.total ? (o.done / o.total) * 100 : 0}
                      sx={{ height: 5, borderRadius: 3, '& .MuiLinearProgress-bar': { borderRadius: 3 } }} />
                  </Box>
                )} />
            ))}
          </Section>
        </Box>
      </Box>
    </Box>
  );
}
